// Vue « Fichiers » transverse : tous les fichiers visibles (RLS), tous dossiers
// confondus, avec recherche, tri et prévisualisation en modale.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';
import { ListToolbar, ToolbarSearch } from '../ui/ListToolbar';
import { useAllCabinetFiles } from '../../hooks/queries/useDossierFiles';
import type { DossierFileWithDossier } from '../../services/files/filesService';
import { FilePreviewModal } from './FilePreviewModal';

import { formatAppDate } from '../../lib/dateFormat';
import { ResizableColumns } from '../ui/ResizableColumns';
type SortKey = 'date' | 'name' | 'type' | 'size';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/** Libellé court du type à partir du MIME. */
function typeLabel(mime: string): string {
  if (!mime) return '—';
  if (mime === 'application/pdf') return 'PDF';
  if (mime.startsWith('image/')) return `Image ${mime.slice(6).toUpperCase()}`;
  if (mime.includes('word') || mime.includes('officedocument.wordprocessing')) return 'Word';
  if (mime.includes('sheet') || mime.includes('excel')) return 'Excel';
  if (mime.startsWith('text/')) return 'Texte';
  const sub = mime.split('/')[1];
  return sub ? sub.toUpperCase() : mime;
}

export function FilesListPage() {
  const { data: files, isLoading } = useAllCabinetFiles();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [preview, setPreview] = useState<DossierFileWithDossier | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = (files ?? []).filter((f) => {
      if (!q) return true;
      return (
        f.filename.toLowerCase().includes(q) ||
        f.dossier?.title.toLowerCase().includes(q) ||
        f.dossier?.number.toLowerCase().includes(q)
      );
    });
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.filename.localeCompare(b.filename);
          break;
        case 'type':
          cmp = (a.mime || '').localeCompare(b.mime || '');
          break;
        case 'size':
          cmp = a.size - b.size;
          break;
        case 'date':
        default:
          cmp = a.created_at.localeCompare(b.created_at);
          break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [files, search, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      setSortAsc(key === 'name'); // nom : A→Z par défaut ; reste : décroissant
    }
  };

  const SortHeader = ({ label, k, className }: { label: string; k: SortKey; className?: string }) => (
    <th className={className}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={cn(
          'inline-flex items-center gap-1 font-semibold uppercase tracking-[0.06em] transition-colors hover:text-cime-primary',
          sortKey === k && 'text-cime-primary',
        )}
      >
        {label}
        {sortKey === k && <span className="text-[10px]">{sortAsc ? '▲' : '▼'}</span>}
      </button>
    </th>
  );

  return (
    <div className="main-container-gdm space-y-3">
      <ListToolbar>
        <ToolbarSearch
          value={search}
          onChange={setSearch}
          placeholder="Rechercher par nom de fichier, dossier ou référence…"
          className="w-[420px]"
        />
      </ListToolbar>

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-cime-text-muted">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm italic text-cime-text-muted text-center py-12">
            {search ? 'Aucun fichier ne correspond à la recherche.' : 'Aucun fichier pour le moment.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="cime-table text-[13px]">
              <ResizableColumns id="files" />
              <thead>
                <tr className="text-left">
                  <SortHeader label="Nom du document" k="name" className="py-2 px-2.5 first:pl-[18px] last:pr-[18px]" />
                  <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px]">Dossier</th>
                  <SortHeader label="Type" k="type" className="py-2 px-2.5 first:pl-[18px] last:pr-[18px]" />
                  <SortHeader label="Poids" k="size" className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-right" />
                  <SortHeader label="Créé le" k="date" className="py-2 px-2.5 first:pl-[18px] last:pr-[18px]" />
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr
                    key={f.id}
                    onClick={() => setPreview(f)}
                    className="cursor-pointer transition-colors"
                  >
                    <td className="py-[9px] px-2.5 first:pl-[18px] last:pr-[18px]">
                      <span className="flex items-center gap-2 min-w-0">
                        <FileText className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" aria-hidden />
                        <span className="max-w-[32rem] truncate font-medium text-cime-text-primary">
                          {f.filename}
                        </span>
                      </span>
                    </td>
                    <td className="py-[9px] px-2.5 first:pl-[18px] last:pr-[18px]">
                      {f.dossier ? (
                        <Link
                          to={`/dossiers/${f.dossier.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="group/dossier block min-w-0 leading-tight"
                        >
                          <span className="block text-[11px] tracking-wide text-neutral-400">{f.dossier.number}</span>
                          <span className="block max-w-[18rem] truncate text-[13px] text-cime-primary group-hover/dossier:underline">
                            {f.dossier.title}
                          </span>
                        </Link>
                      ) : (
                        <span className="text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="py-[9px] px-2.5 first:pl-[18px] last:pr-[18px] text-cime-text-muted">
                      {typeLabel(f.mime)}
                    </td>
                    <td className="py-[9px] px-2.5 first:pl-[18px] last:pr-[18px] text-right tabular-nums text-cime-text-muted">
                      {formatSize(f.size)}
                    </td>
                    <td className="py-[9px] px-2.5 first:pl-[18px] last:pr-[18px] text-cime-text-muted whitespace-nowrap">
                      {formatAppDate(f.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
